import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { type Field, useFields, Validators } from '@pbt/pbt-ui-components'
import type { CustomFieldValidatorState } from '@pbt/pbt-ui-components/src/types/validation'

import type {
  CreateInventoryOrderItemReceiptInput,
  InventoryOrderItemReceipt,
} from '~/api/graphql/generated/types'

import type { ReceiptFieldKey } from '../receivingRequiredFields'

export interface ReceiptFields {
  businessReferenceNumber: Field
  expirationDate: Field
  lotNumber: Field
  notes: Field
  receiveReasonId: Field
  receivedQuantity: Field
  rejected: Field
  serialNumber: Field
}

export interface ReceiptFieldGroup {
  fields: ReceiptFields
  id: string
}

export const extractReceiptInputFromFields = (
  fields: ReceiptFields,
): CreateInventoryOrderItemReceiptInput => ({
  receivedQuantity: fields.receivedQuantity.value,
  lotNumber: fields.lotNumber.value,
  expirationDate: fields.expirationDate.value,
  serialNumber: fields.serialNumber.value,
  businessReferenceNumber: fields.businessReferenceNumber.value,
  notes: fields.notes.value,
  rejected: Boolean(fields.rejected.value),
  ...(fields.receiveReasonId.value
    ? { receiveReasonId: fields.receiveReasonId.value }
    : {}),
})

interface DraftReceipt extends CreateInventoryOrderItemReceiptInput {
  id: string
}

const hasReceiveReason = (
  receipt: InventoryOrderItemReceipt | DraftReceipt,
): receipt is InventoryOrderItemReceipt =>
  'receiveReason' in receipt && receipt.receiveReason?.id !== undefined

const DEFAULT_INPUT_FORMAT = 'MM/DD/YYYY'

const isRequired = (
  key: ReceiptFieldKey,
  requiredFieldKeys: ReceiptFieldKey[],
) => requiredFieldKeys.includes(key)

export const useReceiptFields = (
  receipts: Array<InventoryOrderItemReceipt | DraftReceipt>,
  requiredFieldKeys: ReceiptFieldKey[],
) => {
  const { t } = useTranslation('Admin')
  const requiredMessage = t(
    'Admin:CATALOG.ORDERS.RECEIVING_REQUIRED_FIELD_MESSAGE',
  )
  const expirationInvalidMessage = t(
    'Admin:CATALOG.ORDERS.RECEIVING_EXPIRATION_DATE_INVALID',
  )

  const { fields, validate, touched, updateState } = useFields(
    receipts.flatMap((receipt) => [
      {
        name: `${receipt.id}-receivedQuantity`,
        label: t('Admin:CATALOG.ORDERS.RECEIVING_QUANTITY_LABEL'),
        validators: ['required', 'greaterThanZero'],
        initialValue: receipt.receivedQuantity,
        messages: { required: requiredMessage },
      },
      {
        name: `${receipt.id}-lotNumber`,
        label: t('Admin:LOT_NUMBER'),
        initialValue: receipt.lotNumber,
        ...(isRequired('lotNumber', requiredFieldKeys)
          ? {
              validators: ['required' as const],
              messages: { required: requiredMessage },
            }
          : {}),
      },
      {
        name: `${receipt.id}-expirationDate`,
        label: t('Admin:EXPIRATION_DATE'),
        initialValue: receipt.expirationDate,
        ...(isRequired('expirationDate', requiredFieldKeys)
          ? {
              validators: [
                'required' as const,
                {
                  validator: ({ value }: CustomFieldValidatorState) => {
                    if (value == null || value === '') {
                      return true
                    }
                    const str = String(value)
                    return (
                      moment(str, moment.ISO_8601, true).isValid() ||
                      moment(str, DEFAULT_INPUT_FORMAT, true).isValid()
                    )
                  },
                  validatorName: 'validCalendarDate',
                },
              ],
              messages: {
                required: requiredMessage,
                validCalendarDate: expirationInvalidMessage,
              } as Record<string, string>,
            }
          : {}),
      },
      {
        name: `${receipt.id}-serialNumber`,
        label: t('Admin:SERIAL_NUMBER'),
        initialValue: receipt.serialNumber,
        ...(isRequired('serialNumber', requiredFieldKeys)
          ? {
              validators: ['required' as const],
              messages: { required: requiredMessage },
            }
          : {}),
      },
      {
        name: `${receipt.id}-businessReferenceNumber`,
        label: t('Common:PRACTICE_REFERENCE'),
        initialValue: receipt.businessReferenceNumber,
        ...(isRequired('businessReferenceNumber', requiredFieldKeys)
          ? {
              validators: ['required' as const],
              messages: { required: requiredMessage },
            }
          : {}),
      },
      {
        name: `${receipt.id}-receiveReasonId`,
        label: t('Admin:CATALOG.ORDERS.DAMAGE_REASON_LABEL'),
        initialValue: hasReceiveReason(receipt)
          ? receipt.receiveReason?.id
          : receipt.receiveReasonId,
      },
      {
        name: `${receipt.id}-notes`,
        label: t('Admin:CATALOG.ORDERS.DAMAGE_NOTES_LABEL'),
        initialValue: receipt.notes,
        messages: {
          required: requiredMessage,
        },
        validators: [
          {
            validator: ({ state, value }: CustomFieldValidatorState) => {
              const hasReason = Boolean(state[`${receipt.id}-receiveReasonId`])
              return !hasReason || Validators.notEmptyFormField(value)
            },
            validatorName: 'required',
          },
        ],
      },
      {
        name: `${receipt.id}-rejected`,
        initialValue: Boolean(receipt.rejected),
        type: 'toggle',
      },
    ]),
  )

  return {
    receiptFields: receipts.map(({ id }) => ({
      id,
      fields: {
        receivedQuantity: fields[`${id}-receivedQuantity`],
        lotNumber: fields[`${id}-lotNumber`],
        expirationDate: fields[`${id}-expirationDate`],
        serialNumber: fields[`${id}-serialNumber`],
        businessReferenceNumber: fields[`${id}-businessReferenceNumber`],
        receiveReasonId: fields[`${id}-receiveReasonId`],
        notes: fields[`${id}-notes`],
        rejected: fields[`${id}-rejected`],
      },
    })),
    validate,
    touched,
    updateState,
  }
}
